import type { IdeaRole } from './tradeIdeaVocab'

/**
 * The crosshair guide shown while a trade-idea level is armed: a dashed rule at
 * the pointer's price and a plate saying what to click.
 *
 * When the pointer is on the wrong side of the entry the plate carries the
 * refusal instead — never both. One sentence invites the click; the other
 * refuses it, and showing them together would say nothing clearly. Because the
 * reason is live under the pointer, a click that gets refused is never a click
 * that silently did nothing.
 */

/** Keep the plate off the right edge of the pane. */
const PROMPT_CLAMP = 250

export interface WorkbenchIdeaPromptProps {
  x: number
  y: number
  paneW: number
  text: string
  /** Null while the placement is legal. */
  refusal: string | null
  role: IdeaRole
  color: string
}

export function WorkbenchIdeaPrompt({
  x,
  y,
  paneW,
  text,
  refusal,
  role,
  color,
}: WorkbenchIdeaPromptProps) {
  // The guide takes the risk/reward tone while placing — but the level itself
  // always lands in the idea's colour. Tone belongs to the thing that vanishes.
  const tone = refusal
    ? 'var(--danger)'
    : role === 'sl'
      ? 'var(--danger)'
      : role === 'tp'
        ? 'var(--success)'
        : color

  return (
    <div className="pointer-events-none absolute inset-0 z-[105]" data-idea-prompt>
      <div
        className="absolute left-0 right-0"
        style={{ top: y, borderTop: `1px dashed ${tone}` }}
      >
        <div
          className="absolute whitespace-nowrap rounded px-2 py-1 text-caption text-white"
          style={{
            left: Math.min(x + 14, Math.max(0, paneW - PROMPT_CLAMP)),
            top: -14,
            // Mixed toward black rather than a surface token, so the plate stays
            // readable in light mode instead of washing out.
            background: `color-mix(in srgb, ${tone} 82%, black)`,
            border: `1px solid ${tone}`,
          }}
        >
          {refusal ?? text}
        </div>
      </div>
    </div>
  )
}
